import React, { useEffect, useState, Fragment } from "react";
import { useDispatch } from "react-redux";
import { Row, Col, Card, Button, Table } from "react-bootstrap";
import { Fn_FillListData, Fn_GetReport } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const AdultWorkerRegister = () => {
  const dispatch = useDispatch();

  const readSession = () => ({
    firm: (() => {
      try {
        return JSON.parse(localStorage.getItem("selectedFirm") || "null");
      } catch {
        return null;
      }
    })()
  });

  const [sessionFirm, setSessionFirm] = useState(() => readSession().firm);

  // States
  const [firmList, setFirmList] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [deptList, setDeptList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDept, setSelectedDept] = useState("");

  // Sync session changes from storage
  useEffect(() => {
    const onStorage = () => {
      const s = readSession();
      setSessionFirm(s.firm);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Fetch initial masters
  useEffect(() => {
    const firmApiUrl = `${API_WEB_URLS.MASTER}/0/token/${API_WEB_URLS.FirmMaster}/Id/0`;
    Fn_FillListData(dispatch, setFirmList, "gridData", firmApiUrl);
    Fn_FillListData(dispatch, setDeptList, "gridData", `${API_WEB_URLS.MASTER}/0/token/DepartmentMaster/Id/0`);
  }, [dispatch]);

  // Load report data when inputs change
  useEffect(() => {
    const firmId = sessionFirm?.Id || 0;
    if (firmId) {
      loadReportData(firmId, selectedDept, searchTerm);
    } else {
      setReportData(null);
    }
  }, [sessionFirm, selectedDept, searchTerm]);

  const loadReportData = async (firmId, departmentId, searchQuery) => {
    setIsLoading(true);
    try {
      const fd = new FormData();
      fd.append("F_FirmMaster", firmId);
      fd.append("F_DepartmentMaster", departmentId || 0);
      fd.append("EmployeeSearch", searchQuery || "");

      const records = await Fn_GetReport(
        dispatch,
        () => {},
        "data",
        "GetAdultWorkerRegister/0/token",
        { arguList: { id: 0, formData: fd } },
        true
      );

      let parsedData = null;
      if (records && records.length > 0) {
        const firstKey = Object.keys(records[0])[0];
        if (firstKey) {
          const jsonString = records.map(row => row[firstKey] || "").join("");
          if (jsonString) {
            parsedData = JSON.parse(jsonString);
          }
        }
      }
      setReportData(parsedData);
    } catch (err) {
      console.error("Error loading report data:", err);
      setReportData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const getRecords = () => {
    if (!reportData || !reportData.Records) return [];
    return reportData.Records.map((rec) => ({
      sno: rec.Sno || "",
      name: (rec.EmployeeName || rec.Name || "").toUpperCase(),
      fatherName: (rec.FatherName || "").toUpperCase(),
      address: rec.Address || "",
      group: rec.Group || "",
      designation: rec.Designation || "",
      shift: rec.Shift || "",
      joinDate: rec.JoinDate ? new Date(rec.JoinDate).toLocaleDateString('en-GB') : "",
      leftDate: rec.LeftDate ? new Date(rec.LeftDate).toLocaleDateString('en-GB') : "",
      remark: rec.Remark || ""
    }));
  };

  const records = getRecords();

  // jsPDF export matching rptAdultWorker.pdf layout
  const exportToPDF = () => {
    if (records.length === 0) {
      alert("No data available.");
      return;
    }

    const pdf = new jsPDF("p", "mm", "a4");
    
    // Top Title Block
    pdf.setFontSize(10);
    pdf.setFont("Helvetica", "bold");
    pdf.text(sessionFirm?.Name?.toUpperCase() || "", 105, 12, { align: "center" });

    pdf.setFontSize(8.5);
    pdf.text("FORM NO. 13", 105, 17, { align: "center" });
    pdf.text("REGISTER OF ADULT WORKERS", 105, 22, { align: "center" });

    const body = records.map(row => [
      row.sno,
      `${row.name}\n${row.fatherName}`,
      row.address,
      `${row.group}\n${row.designation}`,
      row.shift,
      `${row.joinDate}\n${row.leftDate}`,
      row.remark
    ]);

    autoTable(pdf, {
      head: [[
        "SNo.",
        "EmployeeName\nFatherName",
        "Address",
        "Group\nDesignation",
        "Shift",
        "Date of Join\nDate of Left",
        "Remark"
      ]],
      body,
      startY: 27,
      margin: { left: 10, right: 10 },
      styles: { fontSize: 7, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.05 },
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: "bold" },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 45 },
        2: { cellWidth: 50 },
        3: { cellWidth: 35 },
        4: { cellWidth: 15 },
        5: { cellWidth: 22 },
        6: { cellWidth: 13 }
      }
    });

    pdf.save(`Register_of_Adult_Workers_${sessionFirm?.Name}.pdf`);
  };

  const printStyles = `
    @media print {
      body * {
        visibility: hidden !important;
      }
      #print-adult-worker-container, #print-adult-worker-container * {
        visibility: visible !important;
      }
      #print-adult-worker-container {
        display: block !important;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        background: #fff;
        color: #000;
        font-family: Arial, sans-serif;
      }
      .no-print {
        display: none !important;
      }
      .adult-worker-print-table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 15px;
      }
      .adult-worker-print-table th, .adult-worker-print-table td {
        border: 1px solid #000;
        padding: 5px;
        font-size: 8px;
        text-align: left;
        vertical-align: top;
      }
      .adult-worker-print-table th {
        background: #f2f2f2;
        font-weight: bold;
      }
    }
  `;

  return (
    <Fragment>
      <style>{printStyles}</style>

      <div className="no-print">
        <PageTitle activeMenu="Adult Workers Register" motherMenu="Reports" pageContent="Register of Adult Workers (Form 13)" />
      </div>

      {/* Filters Card */}
      <Card className="no-print mb-4 shadow-sm border-0" style={{ borderRadius: "10px", background: "#ffffff" }}>
        <Card.Body className="p-4">
          <Row className="align-items-end g-3">
            {/* Firm */}
            <Col lg={4} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>
                Firm <span className="badge bg-light text-success ms-1 border" style={{ fontSize: "11px" }}>🔒 Header Locked</span>
              </label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px", backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                value={sessionFirm?.Name || "Please select Firm from Header"}
                readOnly
                disabled
              />
            </Col>

            {/* Department */}
            <Col lg={3} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Department</label>
              <select
                className="form-select"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                value={selectedDept}
                onChange={(e) => setSelectedDept(e.target.value)}
              >
                <option value="">All Departments</option>
                {deptList.map((d) => (
                  <option key={d.Id} value={d.Id}>{d.Name}</option>
                ))}
              </select>
            </Col>

            {/* Search */}
            <Col lg={3} md={6}>
              <label className="form-label fw-semibold fs-13" style={{ color: "#495057" }}>Search Worker</label>
              <input
                type="text"
                className="form-control"
                style={{ height: "38px", borderRadius: "6px", fontSize: "13px" }}
                placeholder="Name, Father name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Col>

            <Col lg={2} md={6} className="d-flex gap-2 justify-content-end">
              <Button variant="danger" style={{ height: "38px" }} onClick={exportToPDF}>PDF</Button>
              <Button variant="primary" style={{ height: "38px" }} onClick={() => window.print()}>Print</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!sessionFirm?.Id ? (
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center p-5">
            <h5 className="text-warning mb-2 fw-semibold">⚠ Session Setup Required</h5>
            <p className="text-muted mb-0 fs-13">
              Please select a **Firm** to load the register.
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Fragment>
          {/* Web View */}
          <div className="no-print">
            <Card className="shadow-sm border-0" style={{ borderRadius: "10px" }}>
              <Card.Header className="bg-white border-bottom py-3 px-4">
                <div className="text-center">
                  <h4 className="fw-bold mb-1 text-dark fs-15">{sessionFirm?.Name}</h4>
                  <div className="fw-bold text-secondary fs-13">FORM NO. 13</div>
                  <div className="text-muted fs-12">REGISTER OF ADULT WORKERS</div>
                </div>
              </Card.Header>

              <Card.Body className="p-0">
                {isLoading ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="text-muted mt-2 fs-13">Loading workers register...</div>
                  </div>
                ) : records.length === 0 ? (
                  <div className="text-center text-muted p-5">No records found.</div>
                ) : (
                  <div className="table-responsive">
                    <Table bordered hover className="mb-0 text-center" style={{ fontSize: "12px" }}>
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: "5%" }}>SNo.</th>
                          <th>Employee Name / FatherName</th>
                          <th>Address</th>
                          <th>Group / Designation</th>
                          <th>Shift</th>
                          <th>Date of Join / Date of Left</th>
                          <th>Remark</th>
                        </tr>
                      </thead>
                      <tbody>
                        {records.map((row) => (
                          <tr key={row.sno}>
                            <td>{row.sno}</td>
                            <td className="text-start">
                              <strong>{row.name}</strong><br />
                              <span className="text-muted">{row.fatherName}</span>
                            </td>
                            <td className="text-start">{row.address}</td>
                            <td className="text-start">
                              {row.group ? <strong>Group: {row.group}</strong> : ""}<br />
                              {row.designation ? <span className="text-muted">{row.designation}</span> : ""}
                            </td>
                            <td>{row.shift}</td>
                            <td>
                              Join: {row.joinDate}<br />
                              {row.leftDate ? <span className="text-danger">Left: {row.leftDate}</span> : ""}
                            </td>
                            <td>{row.remark}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* Print View Layout */}
          <div id="print-adult-worker-container" className="d-none">
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "12px" }}>
              {sessionFirm?.Name?.toUpperCase()}
            </div>
            <div style={{ textAlign: "center", fontSize: "10px", marginTop: "2px" }}>
              FORM NO. 13
            </div>
            <div style={{ textAlign: "center", fontWeight: "bold", fontSize: "10px", margin: "4px 0" }}>
              REGISTER OF ADULT WORKERS
            </div>

            <table className="adult-worker-print-table">
              <thead>
                <tr>
                  <th>SNo.</th>
                  <th>EmployeeName / FatherName</th>
                  <th>Address</th>
                  <th>Group / Designation</th>
                  <th>Shift</th>
                  <th>Date of Join / Date of Left</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {records.map(row => (
                  <tr key={row.sno}>
                    <td>{row.sno}</td>
                    <td>
                      <strong>{row.name}</strong><br />
                      {row.fatherName}
                    </td>
                    <td>{row.address}</td>
                    <td>
                      {row.group} <br />
                      {row.designation}
                    </td>
                    <td>{row.shift}</td>
                    <td>
                      {row.joinDate} <br />
                      {row.leftDate}
                    </td>
                    <td>{row.remark}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

export default AdultWorkerRegister;
